import isNaN from 'lodash/isNaN';
import isNil from 'lodash/isNil';
import trim from 'lodash/trim';
import { DateTime } from 'luxon';

import {
  AGE_MAX,
  AGE_MIN,
  DATE_IN_PAST_ERROR,
  DOB_RANGE_ERROR,
  EMAIL_ERROR,
  IS_PRESENT_ERROR,
  PHONE_ERROR,
  ZIPCODE_ERROR,
} from './constants';

const EMAIL_REGEX = /^([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})$/i;

const getFormattedDateTime = (dateString: string) => {
  const date = DateTime.fromISO(dateString, { setZone: true }).startOf('day');

  return date;
};

export const getTodayDateTime = () =>
  DateTime.now().setZone('utc', { keepLocalTime: true }).startOf('day');

const isPast = (dateString: string) => DateTime.fromISO(dateString) < getTodayDateTime();

export const isEmail = (value: string | null | undefined): undefined | string =>
  !!value && EMAIL_REGEX.test(trim(value)) ? undefined : EMAIL_ERROR;

export const isDateInPast = (value: string): undefined | string =>
  isNil(value) || isPast(value) ? undefined : DATE_IN_PAST_ERROR;

export const isDateOnOrAfter =
  (date: string, includeDayOfMonth = false) =>
  (value: string): undefined | string => {
    const dateDateTime = getFormattedDateTime(date);
    const valueDateTime = getFormattedDateTime(value);

    const areDatesEqual = dateDateTime.hasSame(valueDateTime, includeDayOfMonth ? 'day' : 'month');

    return isNil(value) || valueDateTime > dateDateTime || areDatesEqual
      ? undefined
      : `Must be on or after ${dateDateTime.toFormat(includeDayOfMonth ? 'DDD' : 'MMMM yyyy')}`;
  };

export const isDateOnOrAfterDOB =
  (dateOfBirth?: string | null) =>
  (value: string): undefined | string => {
    const dobDate = getFormattedDateTime(dateOfBirth ?? '');
    const dobDateString = dobDate.isValid
      ? dateOfBirth!
      : getTodayDateTime().minus({ years: 85 }).toISO()!;

    return isDateOnOrAfter(dobDateString)(value);
  };

export const isDOBRangeValid = (value: string) => {
  const dobDateTime = getFormattedDateTime(value);

  if (isNil(dobDateTime)) {
    return undefined;
  }
  const today = getTodayDateTime();
  const startDate = today.minus({ years: AGE_MAX + 1 });
  const endDate = today.minus({ years: AGE_MIN });

  return isNil(value) || (startDate < dobDateTime && dobDateTime <= endDate)
    ? undefined
    : DOB_RANGE_ERROR;
};

export const isPresent = (value: unknown): undefined | string =>
  !(isNil(value) || isNaN(value) || value === '') ? undefined : IS_PRESENT_ERROR;

export const isPhone = (value: string | null | undefined): undefined | string => {
  if (!value) {
    return PHONE_ERROR;
  }

  const trimmedValue = trim(value);
  const hasTenDigits = /^\d{10}$/.test(trimmedValue);

  if (!hasTenDigits) {
    return PHONE_ERROR;
  }

  const firstNumber = parseInt(trimmedValue[0], 10);
  const validStartDigit = firstNumber > 1;

  if (!validStartDigit) {
    return PHONE_ERROR;
  }

  const hasMiddleFives = trimmedValue.substr(3, 3) === '555';
  let hasReservedNumbers = false;

  if (hasMiddleFives) {
    const endingDigits = parseInt(trimmedValue.substr(6, 4), 10);

    if (endingDigits > 99 && endingDigits < 200) {
      hasReservedNumbers = true;
    }
  }

  return !hasReservedNumbers ? undefined : PHONE_ERROR;
};

export const isZipCode = (value: string) =>
  !!value && /^\d{5}$/.test(trim(value)) ? undefined : ZIPCODE_ERROR;

export const isBetween =
  (min: number, max: number) =>
  (value: number): string | undefined =>
    isNil(value) || (value >= min && value <= max)
      ? undefined
      : `Must be between ${min} and ${max}`;

export const isGreater =
  (min: number) =>
  (value: number): string | undefined =>
    value > min ? undefined : `Must be greater than ${min}`;

export const composeValidators =
  (...validators: Array<(value: any) => undefined | string>) =>
  (value: any) =>
    validators.reduce<string | undefined>(
      (error, validator) => error || validator(value),
      undefined
    );
